/**
 * "How this return was calculated" — the §10 return audit modal, plus the small
 * return/fidelity display helpers it shares with the Returns page.
 *
 * Moved out of pages/ReturnsPage.tsx (team feedback P8, Pedro 2026-10-10) so the
 * Agreement → AuM & Return tab shows the SAME modal. Each caller passes its own
 * fetcher: the Admin Returns page uses /returns/agreement/{id}/audit (admin-only),
 * the Agreement tab uses /agreements/{id}/return-audit (any agreement viewer).
 */
import {
  Badge, Divider, Group, Loader, Modal, Stack, Table, Text, Tooltip,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import type { Fidelity, ReturnAudit, ReturnAuditStep } from '../api/returns';
import type { UUID } from '../api/types';

const EUR0 = new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0, useGrouping: true });
/** Euro-cents → "€ 1 234 567"; null → "—". */
function eur(cents: number | null | undefined): string {
  return cents == null ? '—' : EUR0.format(cents / 100);
}
/** A trace-step numeric (typed `unknown` in the loose step) → signed %; else "—". */
function tracePct(x: unknown): string {
  return typeof x === 'number' ? fmtPct(x) : '—';
}

/** Signed percentage, 2 dp. null → "—". */
export function fmtPct(r: number | null): string {
  if (r == null) return '—';
  return `${r >= 0 ? '+' : ''}${(r * 100).toFixed(2)}%`;
}
export function retColor(r: number | null): string {
  if (r == null) return 'gray';
  if (r > 0) return 'verde.7';
  if (r < 0) return 'bordeaux';
  return 'gray';
}

export function ReturnBadge({ r }: { r: number | null }) {
  return <Badge color={retColor(r)} variant="light">{fmtPct(r)}</Badge>;
}

// Fidelity rung (procedure §8) — the BASIS actually used to value the month, stated
// plainly. Brand hues, none green/red (that pair is reserved for the return SIGN), on a
// confidence ladder exact -> manual -> saa -> gap. Labels match procedure §8 verbatim.
export const FIDELITY: Record<Fidelity, { label: string; color: string; hint: string }> = {
  exact:  { label: 'AuM on date', color: 'petroleo', hint: 'Computed from actual AuM on the required dates — month-end to month-end when there’s no flow, or the AuM on each flow date. No estimate.' },
  manual: { label: 'Manual MTD',  color: 'azul',     hint: 'A cash-flow month valued using the supplied agreement month-to-date return (the desk’s stated MTD).' },
  saa:    { label: 'SAA proxy',   color: 'laranja',  hint: 'A cash-flow month valued from the SAA benchmark’s return over the sub-period — an estimate, used when no actual valuation exists.' },
  gap:    { label: 'No data',     color: 'bordeaux', hint: 'No AuM or estimate could value a cash flow this month, so the return is withheld and flagged — never estimated.' },
};

/** The fidelity pill for one unit-month. Nothing for historical (uploaded) months
 * (they carry no TWR rung — the Historical badge already conveys the source). */
export function FidelityPill({ fidelity, reason }: { fidelity: Fidelity | null; reason?: string | null }) {
  if (!fidelity) return null;
  const f = FIDELITY[fidelity];
  if (!f) return null;
  return (
    <Tooltip label={reason || f.hint} withArrow multiline w={280}>
      <Badge size="xs" variant="light" color={f.color} style={{ cursor: 'help' }}>{f.label}</Badge>
    </Tooltip>
  );
}

// ── §10 regulatory audit — "show working" for one (agreement, month) (Inc 4e-3) ─────────
/** `period` is 'YYYY-MM'; `label` is the month as shown in the modal title. */
export type AuditTarget = { agreementId: UUID; period: string; label: string };

/** One trace step in plain language (procedure §6). The step shapes vary by `step`; the
 * numeric fields are `unknown` on the loose type, so coerce defensively. */
function AuditStepLine({ s }: { s: ReturnAuditStep }) {
  if (s.step === 'opening') {
    return <Text size="sm">▸ Opening{s.anchor === 'first_inflow' ? ' (first inflow)' : ''} on {String(s.date)}: <b>{eur(Number(s.value_cents))}</b></Text>;
  }
  if (s.step === 'flow') {
    const rung = FIDELITY[s.rung as Fidelity]?.label ?? String(s.rung);
    const amt = Number(s.amount_cents);
    return (
      <Text size="sm">
        ▸ Flow {String(s.date)} {amt >= 0 ? '+' : '−'}{eur(Math.abs(amt))}: valued via{' '}
        <b>{rung}</b> (cumulative {tracePct(s.interim_cum_return)}) → sub-period {tracePct(s.subperiod_return)}
      </Text>
    );
  }
  if (s.step === 'eop') {
    return <Text size="sm">▸ To month-end (actual EOM <b>{eur(Number(s.value_cents))}</b>): final sub-period {tracePct(s.subperiod_return)}</Text>;
  }
  if (s.step === 'result') {
    return <Text size="sm" fw={700}>= Month return {tracePct(s.twr)}</Text>;
  }
  if (s.step === 'gap') {
    return <Text size="sm" c="bordeaux">▸ Withheld: {String(s.reason)}</Text>;
  }
  return null;
}

/** Modal: every input + the step-by-step calculation behind one agreement-month return. */
export function ReturnAuditModal({ target, onClose, fetchAudit }: {
  target: AuditTarget | null;
  onClose: () => void;
  fetchAudit: (agreementId: UUID, period: string) => Promise<ReturnAudit>;
}) {
  const q = useQuery({
    queryKey: ['return-audit', target?.agreementId ?? '', target?.period ?? ''] as const,
    queryFn: () => fetchAudit(target!.agreementId, target!.period),
    enabled: !!target,
  });
  const d = q.data;
  return (
    <Modal opened={!!target} onClose={onClose} size="xl" title={`How this return was calculated — ${target?.label ?? ''}`}>
      {q.isLoading && <Group gap="xs"><Loader size="sm" /><Text size="sm" c="dimmed">Loading…</Text></Group>}
      {q.error && <Text size="sm" c="bordeaux">Could not load the audit. {q.error instanceof Error ? q.error.message : ''}</Text>}
      {d && (
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap">
            <Text fw={600}>{d.agreement_code ?? '—'}{d.agreement_mnemonic ? ` · ${d.agreement_mnemonic}` : ''}</Text>
            {d.ratio != null
              ? <Group gap="xs"><FidelityPill fidelity={d.rung} /><ReturnBadge r={d.ratio} /></Group>
              : <Badge color="bordeaux" variant="light" style={{ maxWidth: 360 }}>No data — {d.gap_reason}</Badge>}
          </Group>

          <div>
            <Text fw={600} size="sm" mb={4}>Inputs</Text>
            <Text size="sm" c="dimmed">
              Opening (ΣBOP) {eur(d.inputs.sum_bop_cents)} → month-end (ΣEOM) {eur(d.inputs.sum_eop_cents)}
              {d.inputs.saa_profile ? ` · SAA benchmark ${d.inputs.saa_profile.code}` : ''}
            </Text>
            <Table withTableBorder striped verticalSpacing="xs" mt="xs">
              <Table.Thead><Table.Tr>
                <Table.Th>Account</Table.Th>
                <Table.Th ta="right">Prior EOM</Table.Th>
                <Table.Th ta="right">Month-end</Table.Th>
                <Table.Th>Flows</Table.Th>
              </Table.Tr></Table.Thead>
              <Table.Tbody>
                {d.inputs.contributing_accounts.map((a) => (
                  <Table.Tr key={a.account_id}>
                    <Table.Td><Text size="sm">{a.label}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{eur(a.prior_eom_cents)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{eur(a.eom_cents)}</Text></Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {a.flows.length
                          ? a.flows.map((f) => `${f.date.slice(0, 10)} ${f.kind === 'inflow' ? '+' : '−'}${eur(Math.abs(f.amount_cents))}`).join(', ')
                          : '—'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {d.inputs.manual_mtds.length > 0 && (
              <Text size="xs" c="dimmed" mt={4}>
                Supplied agreement MTDs: {d.inputs.manual_mtds.map((m) => `${m.flow_date} ${(m.mtd_return * 100).toFixed(2)}%`).join(' · ')}
              </Text>
            )}
          </div>

          <Divider />

          <div>
            <Text fw={600} size="sm" mb={4}>The calculation, step by step</Text>
            <Stack gap={4}>
              {d.trace.map((s, i) => <AuditStepLine key={i} s={s} />)}
            </Stack>
            <Text size="xs" c="dimmed" mt="xs">
              Time-weighted (TWR): the month is split at each flow and the sub-period returns are
              geometrically linked. Where no actual valuation existed at a flow, the basis used is
              shown (Manual MTD / SAA proxy); if none did, the month is withheld.
            </Text>
          </div>
        </Stack>
      )}
    </Modal>
  );
}
